/**
 * HubSettingsPage — HUB-REDESIGN P5.
 *
 * Settings surface reshelled from the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/other-pages.jsx
 * (SettingsPage, lines 298-352).
 *
 * Wires:
 *   /hub/admins   — list of super_admin / admin hub users (Hub access card)
 *   /hub/flags    — global feature-flag rollout view (display-only toggle)
 *
 * Stubs (intentionally non-functional for P5):
 *   - The flag toggle visual is render-only — no /hub/flags/:key/toggle
 *     endpoint exists yet. Marked with title="Toggle endpoint not yet wired"
 *     so reviewers know it's display-only.
 *   - The admin row "more-horizontal" button is a noop placeholder — the
 *     role-edit / remove-admin menu lands in a later row.
 *
 * Also folds the orphaned /api-keys surface into Settings so super-admin
 * API token management is discoverable from the hub sidebar (the route
 * is intentionally not in HUB_NAV).
 */
import { useEffect, useState, useCallback } from 'react'
import { Card, Pill, Endpoint, Icon } from './components'
import { IconButton } from '@/components/atoms/IconButton'
import {
  hubAdmins, hubGlobalFlags, hubPricing, hubPricingSubscribers, hubIntegrations, hubSupportTickets,
  type HubAdminRow, type HubGlobalFlagRow, type HubPricingTier,
  type HubPricingSubscriberPlan, type IntegrationStatus, type SupportTicketStats,
} from './api'

export default function HubSettingsPage() {
  const [admins, setAdmins] = useState<HubAdminRow[]>([])
  const [flags, setFlags] = useState<HubGlobalFlagRow[]>([])

  const refresh = useCallback(async () => {
    const [a, f] = await Promise.all([
      hubAdmins.list().catch(() => null),
      hubGlobalFlags.list().catch(() => null),
    ])
    if (a) setAdmins(a.admins)
    if (f) setFlags(f.flags)
  }, [])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 60_000)
    return () => clearInterval(id)
  }, [refresh])

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <HubAccessCard admins={admins} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        <GlobalFlagsCard flags={flags} />
        <IntegrationsCard />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        <SupportTicketsCard />
      </div>

      <PricingCard />

      <ApiKeysCard />
    </div>
  )
}

// ── Card: Hub access ────────────────────────────────────────────────────
function HubAccessCard({ admins }: { admins: HubAdminRow[] }) {
  return (
    <Card
      title={`Hub access · ${admins.length} admin${admins.length === 1 ? '' : 's'}`}
      endpoint="/hub/admins"
      bodyPad={false}
    >
      <table className="tbl">
        <thead>
          <tr>
            <th>Email</th>
            <th style={{ width: 140 }}>Role</th>
            <th style={{ width: 100 }}>Last seen</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {admins.length === 0 && (
            <tr>
              <td colSpan={4} style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '12px 0' }}>
                No hub admins yet.
              </td>
            </tr>
          )}
          {admins.map(a => (
            <tr key={a.id}>
              <td>{a.email}</td>
              <td>
                {a.role === 'super_admin'
                  ? <Pill variant="admin">SUPER ADMIN</Pill>
                  : <Pill variant="ghost">{capitalize(a.role)}</Pill>}
              </td>
              <td className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {a.last_login_at ? relTime(a.last_login_at) : '—'}
              </td>
              <td style={{ textAlign: 'right' }}>
                <IconButton
                  aria-label="Admin actions"
                  title="Admin actions menu — not yet wired"
                  size="sm"
                  onClick={() => { /* placeholder — admin menu not yet wired */ }}
                >
                  <Icon name="more-horizontal" size={12} />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

// ── Card: Global feature flags ──────────────────────────────────────────
function GlobalFlagsCard({ flags }: { flags: HubGlobalFlagRow[] }) {
  return (
    <Card title="Global feature flags" endpoint="/hub/flags" bodyPad={false}>
      <div>
        {flags.length === 0 && (
          <div style={{ padding: '12px', color: 'var(--text-dim)', fontSize: 12 }}>
            No global flags defined.
          </div>
        )}
        {flags.map((f, i) => {
          const on = f.companies_enabled > 0
          const scopeLabel = (f.description || '').slice(0, 12) || 'global'
          return (
            <div
              key={f.key}
              className="row"
              style={{
                padding: '9px 12px',
                borderTop: i ? '1px solid var(--border)' : 0,
                gap: 8,
              }}
            >
              <span className="mono" style={{ fontSize: 11.5, flex: 1 }}>{f.key}</span>
              <Pill variant="ghost" style={{ fontSize: 9.5 }}>{scopeLabel}</Pill>
              <span
                title="Toggle endpoint not yet wired"
                style={{
                  width: 28,
                  height: 16,
                  borderRadius: 99,
                  position: 'relative',
                  background: on ? 'var(--accent)' : 'var(--surface-3)',
                  cursor: 'default',
                  display: 'inline-block',
                }}
                aria-label={`${f.key} ${on ? 'enabled' : 'disabled'} (display only)`}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: on ? 14 : 2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: 'var(--bg-elev)',
                  }}
                />
              </span>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// ── Card: Integrations (live from /hub/integration-health) ──────────────
function IntegrationsCard() {
  const [items, setItems] = useState<IntegrationStatus[]>([])

  useEffect(() => {
    hubIntegrations.health()
      .then(r => setItems(r.integrations))
      .catch(() => {})
    const id = setInterval(() => {
      hubIntegrations.health().then(r => setItems(r.integrations)).catch(() => {})
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  return (
    <Card title="Integrations" endpoint="/hub/integration-health" bodyPad={false}>
      <div>
        {items.length === 0 && (
          <div style={{ padding: '12px', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
            Loading…
          </div>
        )}
        {items.map((r, i) => (
          <div
            key={r.key}
            className="row"
            style={{
              padding: '9px 12px',
              borderTop: i ? '1px solid var(--border)' : 0,
              gap: 8,
            }}
          >
            <span className={`dot ${r.tone}`} aria-hidden />
            <span style={{ flex: 1, fontSize: 12 }}>{r.label}</span>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              {r.note}{r.mode === 'live' ? ' · live' : r.mode === 'test' ? ' · test' : ''}
            </span>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ── Card: API keys link strip ───────────────────────────────────────────
function ApiKeysCard() {
  return (
    <Card title="API keys" endpoint="/hub/api-keys" bodyPad={false}>
      <div
        className="row"
        style={{ padding: '10px 12px', gap: 10 }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12 }}>Manage super-admin API tokens</div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
            Audit, create, and revoke super-admin API tokens.
          </div>
        </div>
        <a
          href="/api-keys"
          className="btn ghost"
          style={{ fontSize: 11, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          Open
          <Icon name="arrow-right" size={10} />
        </a>
      </div>
    </Card>
  )
}

// ── Card: Pricing tiers ─────────────────────────────────────────────────
function PricingCard() {
  const [tiers, setTiers] = useState<HubPricingTier[]>([])
  const [subsByPlan, setSubsByPlan] = useState<Record<string, number>>({})
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState<string | null>(null)
  const [editing, setEditing] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<Partial<HubPricingTier>>({})
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [showMigrate, setShowMigrate] = useState(false)

  const load = useCallback(async () => {
    const [r, s] = await Promise.all([
      hubPricing.list().catch(() => null),
      hubPricingSubscribers.list().catch(() => null),
    ])
    if (r) setTiers(r.tiers)
    if (s) {
      const m: Record<string, number> = {}
      s.plans.forEach((p: HubPricingSubscriberPlan) => { m[p.plan] = p.count })
      setSubsByPlan(m)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const doSync = async () => {
    setSyncing(true)
    setSyncMsg(null)
    try {
      const r = await hubPricing.sync()
      setSyncMsg(`${r.synced} synced (${r.created} new, ${r.updated} updated${r.skipped ? ', ' + r.skipped + ' skipped' : ''})`)
      await load()
    } catch {
      setSyncMsg('Sync failed')
    }
    setSyncing(false)
  }

  const startEdit = (t: HubPricingTier) => {
    setEditing(t.id)
    setEditDraft({
      name: t.name, slug: t.slug, per_label: t.per_label,
      tagline: t.tagline, bullets: [...t.bullets],
      cta_label: t.cta_label, cta_href: t.cta_href,
      emphasis: t.emphasis, display_order: t.display_order,
    })
  }

  const saveEdit = async () => {
    if (!editing) return
    setSaving(true)
    try {
      await hubPricing.update(editing, editDraft)
      setEditing(null)
      await load()
    } catch { /* noop */ }
    setSaving(false)
  }

  const toggleActive = async (t: HubPricingTier) => {
    await hubPricing.update(t.id, { active: !t.active }).catch(() => null)
    await load()
  }

  const duplicate = async (t: HubPricingTier) => {
    const suffix = `-copy`
    await hubPricing.create({
      name: t.name + ' (copy)',
      slug: t.slug + suffix,
      per_label: t.per_label,
      tagline: t.tagline,
      bullets: [...t.bullets],
      cta_label: t.cta_label,
      cta_href: t.cta_href,
      emphasis: false,
      display_order: t.display_order + 1,
    }).catch(() => null)
    await load()
  }

  const startCreate = () => {
    setCreating(true)
    setEditing(null)
    setEditDraft({
      name: '', slug: '', per_label: 'per seat / mo',
      tagline: '', bullets: [],
      cta_label: 'Get started', cta_href: '',
      emphasis: false, display_order: tiers.length,
    })
  }

  const saveCreate = async () => {
    setSaving(true)
    try {
      await hubPricing.create(editDraft)
      setCreating(false)
      await load()
    } catch { /* noop */ }
    setSaving(false)
  }

  const fmtCents = (c: number | null | undefined) =>
    c == null ? '—' : `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`

  return (
    <Card
      title={`Pricing tiers · ${tiers.length}`}
      endpoint="/hub/pricing/tiers"
      bodyPad={false}
      right={
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {syncMsg && (
            <span style={{ fontSize: 10, color: 'var(--text-dim)', maxWidth: 220 }}>{syncMsg}</span>
          )}
          <button
            className="btn ghost"
            style={{ fontSize: 11, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => setShowMigrate(v => !v)}
          >
            <Icon name="refresh-cw" size={10} />
            Migrate plan
          </button>
          <button
            className="btn ghost"
            style={{ fontSize: 11, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={doSync}
            disabled={syncing}
          >
            <Icon name="refresh-cw" size={10} />
            {syncing ? 'Syncing…' : 'Sync Stripe'}
          </button>
          <button
            className="btn ghost"
            style={{ fontSize: 11, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={startCreate}
            disabled={creating}
          >
            <Icon name="plus" size={10} />
            New tier
          </button>
        </div>
      }
    >
      {creating && (
        <TierEditForm
          draft={editDraft}
          onChange={setEditDraft}
          onSave={saveCreate}
          onCancel={() => setCreating(false)}
          saving={saving}
          isNew
        />
      )}
      <table className="tbl">
        <thead>
          <tr>
            <th>Name</th>
            <th style={{ width: 76 }}>Monthly</th>
            <th style={{ width: 76 }}>Annual</th>
            <th style={{ width: 90 }}>Per</th>
            <th style={{ width: 36, textAlign: 'center' }}>Emph</th>
            <th style={{ width: 36, textAlign: 'center' }}>Ord</th>
            <th style={{ width: 46, textAlign: 'center' }}>Subs</th>
            <th style={{ width: 54 }}>Status</th>
            <th style={{ width: 90 }}>Stripe</th>
            <th style={{ width: 70 }}>Synced</th>
            <th style={{ width: 88 }}></th>
          </tr>
        </thead>
        <tbody>
          {tiers.length === 0 && !creating && (
            <tr>
              <td colSpan={11} style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '12px 0' }}>
                No pricing tiers. Sync from Stripe or create one manually.
              </td>
            </tr>
          )}
          {tiers.map(t => (
            <>
              <tr key={t.id} style={{ opacity: t.active ? 1 : 0.45 }}>
                <td>
                  <div style={{ fontSize: 12 }}>{t.name}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>{t.slug}</div>
                </td>
                <td className="mono" style={{ fontSize: 11 }}>{fmtCents(t.price_monthly_cents)}</td>
                <td className="mono" style={{ fontSize: 11 }}>{fmtCents(t.price_annual_cents)}</td>
                <td style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t.per_label || '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  {t.emphasis && <Pill variant="info" style={{ fontSize: 9 }}>★</Pill>}
                </td>
                <td className="mono" style={{ fontSize: 11, textAlign: 'center' }}>{t.display_order}</td>
                <td className="mono" style={{ fontSize: 11, textAlign: 'center' }}>
                  {subsByPlan[t.slug] ?? 0}
                </td>
                <td>
                  <Pill variant={t.active ? 'ok' : 'ghost'} style={{ fontSize: 9 }}>
                    {t.active ? 'ACTIVE' : 'OFF'}
                  </Pill>
                </td>
                <td className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                  {t.stripe_product_id ? '…' + t.stripe_product_id.slice(-8) : '—'}
                </td>
                <td style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                  {t.last_synced_at ? relTime(t.last_synced_at) : '—'}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 3, justifyContent: 'flex-end' }}>
                    <IconButton
                      aria-label="Edit tier"
                      size="sm"
                      onClick={() => editing === t.id ? setEditing(null) : startEdit(t)}
                    >
                      <Icon name="pencil" size={11} />
                    </IconButton>
                    <IconButton
                      aria-label={t.active ? 'Disable tier' : 'Enable tier'}
                      title={t.active ? 'Disable' : 'Enable'}
                      size="sm"
                      onClick={() => toggleActive(t)}
                    >
                      <Icon name={t.active ? 'eye-off' : 'eye'} size={11} />
                    </IconButton>
                    <IconButton
                      aria-label="Duplicate tier"
                      title="Duplicate"
                      size="sm"
                      onClick={() => duplicate(t)}
                    >
                      <Icon name="copy" size={11} />
                    </IconButton>
                  </div>
                </td>
              </tr>
              {editing === t.id && (
                <tr key={`edit-${t.id}`}>
                  <td colSpan={11} style={{ padding: 0, borderTop: 0 }}>
                    <TierEditForm
                      draft={editDraft}
                      onChange={setEditDraft}
                      onSave={saveEdit}
                      onCancel={() => setEditing(null)}
                      saving={saving}
                    />
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
      {showMigrate && (
        <MigratePlanPanel tiers={tiers} onDone={load} />
      )}
    </Card>
  )
}

function MigratePlanPanel({ tiers, onDone }: { tiers: HubPricingTier[]; onDone: () => void }) {
  const [fromPlan, setFromPlan] = useState('')
  const [toPlan, setToPlan] = useState('')
  const [dryRun, setDryRun] = useState(true)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const run = async () => {
    if (!fromPlan || !toPlan) return
    setRunning(true)
    setResult(null)
    try {
      const r = await hubPricingSubscribers.migrate(fromPlan, toPlan, dryRun)
      if (dryRun) {
        setResult(`Dry run: ${r.migrated} companies would move from "${r.from_plan}" → "${r.to_plan}"`)
      } else {
        setResult(`Migrated ${r.migrated} companies from "${r.from_plan}" → "${r.to_plan}"`)
        onDone()
      }
    } catch (e: unknown) {
      setResult(`Error: ${e instanceof Error ? e.message : 'unknown'}`)
    }
    setRunning(false)
  }

  const slugs = tiers.map(t => t.slug)

  return (
    <div style={{
      padding: '12px 14px',
      background: 'var(--surface-2)',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      alignItems: 'flex-end',
      gap: 10,
      flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', flexBasis: '100%' }}>
        Bulk plan migration
      </span>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 140 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>From plan</span>
        <input
          className="input"
          list="migrate-from-plans"
          value={fromPlan}
          onChange={e => setFromPlan(e.target.value)}
          placeholder="starter"
          style={{ fontSize: 11 }}
        />
        <datalist id="migrate-from-plans">
          {slugs.map(s => <option key={s} value={s} />)}
        </datalist>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 140 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>To plan</span>
        <input
          className="input"
          list="migrate-to-plans"
          value={toPlan}
          onChange={e => setToPlan(e.target.value)}
          placeholder="pro"
          style={{ fontSize: 11 }}
        />
        <datalist id="migrate-to-plans">
          {slugs.map(s => <option key={s} value={s} />)}
        </datalist>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, paddingBottom: 2 }}>
        <input
          type="checkbox"
          checked={dryRun}
          onChange={e => setDryRun(e.target.checked)}
        />
        Dry run
      </label>
      <button
        className="btn"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={run}
        disabled={running || !fromPlan || !toPlan || fromPlan === toPlan}
      >
        {running ? 'Running…' : dryRun ? 'Preview' : 'Migrate'}
      </button>
      {result && (
        <span style={{ fontSize: 11, color: 'var(--text-dim)', flexBasis: '100%' }}>{result}</span>
      )}
    </div>
  )
}

function TierEditForm({
  draft, onChange, onSave, onCancel, saving, isNew = false,
}: {
  draft: Partial<HubPricingTier>
  onChange: (d: Partial<HubPricingTier>) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
  isNew?: boolean
}) {
  return (
    <div style={{
      padding: '12px 14px',
      background: 'var(--surface-2)',
      borderTop: '1px solid var(--border)',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 8,
    }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Name</span>
        <input
          className="input"
          value={draft.name ?? ''}
          onChange={e => onChange({ ...draft, name: e.target.value })}
          placeholder="Starter"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Slug</span>
        <input
          className="input"
          value={draft.slug ?? ''}
          onChange={e => onChange({ ...draft, slug: e.target.value })}
          placeholder="starter"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Per label</span>
        <input
          className="input"
          value={draft.per_label ?? ''}
          onChange={e => onChange({ ...draft, per_label: e.target.value })}
          placeholder="per seat / mo"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: '1 / -1' }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Tagline</span>
        <input
          className="input"
          value={draft.tagline ?? ''}
          onChange={e => onChange({ ...draft, tagline: e.target.value })}
          placeholder="For growing construction teams"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>CTA label</span>
        <input
          className="input"
          value={draft.cta_label ?? ''}
          onChange={e => onChange({ ...draft, cta_label: e.target.value })}
          placeholder="Get started"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>CTA href</span>
        <input
          className="input"
          value={draft.cta_href ?? ''}
          onChange={e => onChange({ ...draft, cta_href: e.target.value })}
          placeholder="/signup?plan=starter"
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Display order</span>
        <input
          className="input"
          type="number"
          value={draft.display_order ?? 0}
          onChange={e => onChange({ ...draft, display_order: parseInt(e.target.value) || 0 })}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, gridColumn: '1 / -1' }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Bullets (one per line)</span>
        <textarea
          className="input"
          style={{ minHeight: 72, resize: 'vertical', fontFamily: 'monospace', fontSize: 11 }}
          value={(draft.bullets ?? []).join('\n')}
          onChange={e => onChange({ ...draft, bullets: e.target.value.split('\n').filter(l => l.trim()) })}
          placeholder={'Unlimited projects\n5 team members\nPriority support'}
        />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, gridColumn: '1 / -1' }}>
        <input
          type="checkbox"
          checked={draft.emphasis ?? false}
          onChange={e => onChange({ ...draft, emphasis: e.target.checked })}
        />
        <span style={{ fontSize: 12 }}>Emphasize (most popular / recommended)</span>
      </label>
      <div style={{ display: 'flex', gap: 8, gridColumn: '1 / -1', justifyContent: 'flex-end', marginTop: 2 }}>
        <button className="btn ghost" style={{ fontSize: 11 }} onClick={onCancel}>Cancel</button>
        <button className="btn" style={{ fontSize: 11 }} onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create tier' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}

// ── Card: Support tickets ───────────────────────────────────────────────
function SupportTicketsCard() {
  const [stats, setStats] = useState<SupportTicketStats | null>(null)

  useEffect(() => {
    hubSupportTickets.stats()
      .then(r => setStats(r))
      .catch(() => {})
    const id = setInterval(() => {
      hubSupportTickets.stats().then(r => setStats(r)).catch(() => {})
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  return (
    <Card title="Support tickets" endpoint="/api/v1/hub/help/tickets" bodyPad={false}>
      <div style={{ padding: 12, display: 'flex', gap: 20 }}>
        {stats === null ? (
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Loading…</div>
        ) : (
          <>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Total</div>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{stats.total}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Filed as issues</div>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{stats.filed}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Unfiled</div>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{stats.unfiled}</div>
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

// ── helpers ─────────────────────────────────────────────────────────────
function capitalize(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0 || !isFinite(ms)) return '—'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.floor(hr / 24)
  return `${day}d`
}
